/* eslint-disable */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useStore } from '../../store/store';
import { useNav } from '../../hooks/nav';
import Analytics from '@web-solutions/module-analytics';
import { t } from '@web-solutions/module-localization';
import { Typography } from 'src/onboarding/components/typography-voery/Typography';
import { BackIcon } from 'src/onboarding/assets/icons/BackIcon';
import { SearchInput } from 'src/onboarding/components/ui/SearchInput/SearchInput';
import { List } from 'src/onboarding/components/ui/List/List';
import { ALL_LANGUAGES_KEYS, LOCALE_TO_KEY_MAP } from 'src/onboarding/constants/allLanguages';

function Page() {
  const nativeLanguage = useStore((state) => state.nativeLanguage);
  const setNativeLanguage = useStore((state) => state.setNativeLanguage);
  const [allLanguages, setAllLanguages] = useState<string[]>([]);
  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { onNext, onPrev, currentIndex } = useNav();

  const localeCode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const variant = params.get('variant');

    if (variant) {
      return variant;
    }

    return navigator.languages?.[0] || navigator.language;
  }, []);

  const getLanguageKeyFromLocale = useCallback((code: string): string => {
    const normalized = code.toLowerCase();

    if (LOCALE_TO_KEY_MAP[normalized]) {
      return LOCALE_TO_KEY_MAP[normalized];
    }

    const short = normalized.split('-')[0];
    if (LOCALE_TO_KEY_MAP[short]) {
      return LOCALE_TO_KEY_MAP[short];
    }

    return 'english';
  }, []);

  const getTranslatedLanguage = useCallback(
    (key: string): string => {
      return t(`onboarding.common.languages.${key}`);
    },
    [t]
  );

  useEffect(() => {
    if (nativeLanguage) return;

    const languageKey = getLanguageKeyFromLocale(localeCode);
    const translatedLanguage = getTranslatedLanguage(languageKey);
    setNativeLanguage(translatedLanguage);
  }, [localeCode, nativeLanguage]);

  useEffect(() => {
    const translated = ALL_LANGUAGES_KEYS.map((key) => t(`onboarding.common.languages.${key}`));
    setAllLanguages(translated);
  }, []);

  const mostPopularLang = useMemo(() => {
    const popularKeys = ['chineseSimpl', 'spanish', 'hindi', 'arabic', 'french'];

    const popularTranslations = popularKeys.map((key) => t(`onboarding.common.languages.${key}`));

    const userLanguageKey = getLanguageKeyFromLocale(localeCode);
    const userLanguage = getTranslatedLanguage(userLanguageKey);

    return [userLanguage, ...popularTranslations.filter((lang) => lang !== userLanguage)];
  }, [localeCode, getLanguageKeyFromLocale, getTranslatedLanguage]);

  const handleLanguageChange = useCallback(
    (value: string) => {
      setNativeLanguage(value);
      Analytics.trackEvent(`step${currentIndex + 1}`, 'complete', {
        page: 'native-language',
        answer: value,
      });
      setTimeout(() => {
        onNext();

      }, 500)
    },
    [currentIndex, setNativeLanguage]
  );

  const filteredLanguages = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const q = searchQuery.toLowerCase();

    return allLanguages.filter((lang) => lang.toLowerCase().startsWith(q));
  }, [searchQuery, allLanguages]);

  return (
    <div className='page'>
      {currentIndex !== 0 && (
        <div className='back' onClick={onPrev}>
          <BackIcon />
        </div>
      )}
      <div className='wrapper-info'>
        <Typography variant='h1-bold'>{t('onboarding.nativeLanguage.title')}</Typography>
        <Typography variant='body-regular' className='description'>
          {t('onboarding.nativeLanguage.subTitle')}
        </Typography>
      </div>
      <div className='controls' style={{ paddingBottom: '70px' }}>
        <List
          title={t('onboarding.nativeLanguage.mostPopular')}
          onChange={handleLanguageChange}
          defaultValue={nativeLanguage}
          options={mostPopularLang}
        />

        <List
          title={t('onboarding.nativeLanguage.allLanguages')}
          onChange={handleLanguageChange}
          defaultValue={nativeLanguage}
          options={allLanguages}
        />
        <div className='overlay'></div>
      </div>
      {!searchActive && (
        <div className='next-wrapper'>
          <SearchInput onActivate={() => setSearchActive(true)} />
        </div>
      )}

      {searchActive && (
        <div className='search-layer'>
          <SearchInput
            active
            onDeactivate={() => setSearchActive(false)}
            onChangeValue={setSearchQuery}
          />

          <div className='search-results'>
            <List
              onChange={(value) => {
                setTimeout(() => {
                  handleLanguageChange(value);
                }, 500);
              }}
              options={filteredLanguages}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Page;
